//Libs
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useMemo, useEffect, ReactNode, useCallback } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Timestamp } from "firebase/firestore";
import debounce from "lodash/debounce";

//Local
import {
  Customer,
  customerTypes,
  BillingInfo,
  ExistingCustomerWithLocations,
} from "../../models/customer";
import { formatCustomerName } from "../../assets/js/formatCustomerName";
import { logger as devLogger } from "../../logging";
import * as strings from "../../strings";
import { ErrorMessage } from "../ErrorMessage";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseInputSelect from "../BaseInputSelect";
import BaseInputText from "../BaseInputText";
import StyledMessage from "../StyledMessage";
import { ExistingSiteKey } from "../../models/site-key";
import BaseInputTextArea from "../BaseInputTextArea";
import { handleTags } from "../../assets/js/HandleTags";
import StyledSwitchGroup from "../StyledSwitchGroup";
import { phoneUtils } from "../../utils/phoneUtils";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import EditBillingInfoDialog from "./EditBillingInfoDialog";
import { getBillingInfoFromCustomerAndLocations } from "../../models/customer";
import { TemporaryLocation } from "./AddEditCustomerLocationDialog";
import ExistingCustomerMatches from "./ExistingCustomerMatches";
import { typesenseCustomersQuery } from "../../utils/typesenseQueries";

interface Props {
  siteKeyCustomizations: ExistingSiteKey["customizations"];
  className: string;
  handleSave: (
    formValues: Omit<
      Customer,
      | "timestampCreated"
      | "timestampLastModified"
      | "createdBy"
      | "lastModifiedBy"
    >,
  ) => Promise<void>;
  setSearchName?: React.Dispatch<React.SetStateAction<string>>;
  setAddCustomerFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetMembershipStates: () => void;
  /* CHILDREN */
  children?: {
    AddNewCustomerLocationDialog: (customerType: Customer["type"]) => ReactNode;
  };
  locations?: TemporaryLocation[];
  /* NEW PROPS FOR CUSTOMER SEARCH */
  typesenseSearchKey?: string | null;
  onSelectExistingCustomer?: (customer: ExistingCustomerWithLocations) => void;
}

export const CustomerFormSchema = z
  .object({
    name: z.string().max(200),
    firstName: z
      .string()
      .min(1, { message: "First name is required" })
      .max(200),
    lastName: z.string().min(1, { message: "Last name is required" }).max(200),
    email: z.string().email().nullable().or(z.literal("")),
    phone: z.string().max(200),
    notes: z.string().max(1000),
    type: z.enum(customerTypes),
    tags: z.string().min(0).max(2000),
    isTaxExempt: z.boolean(),
    isTaxExemptGST: z.boolean().optional(),
    isTaxExemptPST: z.boolean().optional(),
    doNotService: z.boolean().optional(),
    templateNotes: z.string().max(1000).optional(),
    defaultSendBookingConfirmationEmail: z.boolean(),
    defaultSendJobReminderEmail: z.boolean(),
  })
  .superRefine((customer, ctx) => {
    if (customer.type === "commercial" && customer.name.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: "string",
        inclusive: true,
        message: "Business name is required",
        path: ["name"],
      });
    }
  });

export type CustomerFormState = z.infer<typeof CustomerFormSchema>;

export default function AddNewCustomerForm({
  handleSave,
  setAddCustomerFormOpen,
  setSearchName,
  resetMembershipStates,
  siteKeyCustomizations,
  locations = [],
  typesenseSearchKey,
  onSelectExistingCustomer,
  ...props
}: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [showBillingInfoDialog, setShowBillingInfoDialog] =
    useState<boolean>(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);

  // Customer search state
  const [customerMatches, setCustomerMatches] = useState<
    ExistingCustomerWithLocations[]
  >([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showMatches, setShowMatches] = useState<boolean>(false);

  const customerFormDefaultValues: CustomerFormState = useMemo(() => {
    return {
      name: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      notes: "",
      type: "residential",
      tags: "",
      isTaxExempt: false,
      templateNotes: "",
      defaultSendBookingConfirmationEmail: false,
      defaultSendJobReminderEmail: false,
    };
  }, []);

  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
    getValues,
  } = useForm<CustomerFormState>({
    defaultValues: customerFormDefaultValues,
    resolver: zodResolver(CustomerFormSchema),
    mode: "onChange",
  });

  const watchType = useWatch({
    control: control,
    name: "type",
  });

  useEffect(() => {
    reset(customerFormDefaultValues);
  }, [customerFormDefaultValues, reset]);

  useEffect(() => {
    reset({
      name: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      notes: "",
      type: watchType,
      tags: "",
      isTaxExempt: false,
      templateNotes: "",
      defaultSendBookingConfirmationEmail: false,
      defaultSendJobReminderEmail: false,
    });
    if (setSearchName != null) {
      setSearchName("");
    }
  }, [setSearchName, reset, watchType]);

  // Add a useEffect to handle location changes and set default billing info
  useEffect(() => {
    if (locations.length === 1 && !billingInfo) {
      // Auto-generate billing info from the location when a single location is added
      const defaultInfo = getDefaultBillingInfo();
      if (defaultInfo) {
        console.log(
          "Generated default billing info from location:",
          defaultInfo,
        );
      }
    }
  }, [locations, billingInfo]);

  // Debounced search function for finding existing customers
  const debouncedSearchCustomers = useCallback(
    debounce(async (searchQuery: string) => {
      if (
        !typesenseSearchKey ||
        !searchQuery.trim() ||
        searchQuery.trim().length < 2
      ) {
        setCustomerMatches([]);
        setShowMatches(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = await typesenseCustomersQuery(
          typesenseSearchKey,
          searchQuery.trim(),
        );
        if (results && results.length > 0) {
          setCustomerMatches(results);
          setShowMatches(true);
        } else {
          setCustomerMatches([]);
          setShowMatches(false);
        }
      } catch (error) {
        console.error("Error searching for customers:", error);
        setCustomerMatches([]);
        setShowMatches(false);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [typesenseSearchKey],
  );

  /* fn that handle all the states that needs to be reset when the dialog is closed */
  function closeAndReset() {
    reset();
    setDisplayError(false);
    setAddCustomerFormOpen(false);
    resetMembershipStates();
    setBillingInfo(null); // Reset billing info when closing
    setCustomerMatches([]);
    setShowMatches(false);
    setIsSearching(false);
    if (setSearchName != null) {
      setSearchName("");
    }
  }

  function handleSearchName() {
    const name = formatCustomerName({
      type: getValues("type"),
      name: getValues("name"),
      firstName: getValues("firstName"),
      lastName: getValues("lastName"),
    });
    if (setSearchName != null) {
      setSearchName(name);
    }

    // Trigger customer search
    if (name.trim().length >= 2) {
      debouncedSearchCustomers(name);
    } else {
      setCustomerMatches([]);
      setShowMatches(false);
    }
  }

  // Handle selecting an existing customer
  const handleSelectExistingCustomer = (
    customer: ExistingCustomerWithLocations,
  ) => {
    if (onSelectExistingCustomer) {
      onSelectExistingCustomer(customer);
    }
    closeAndReset();
  };

  // Handle continuing with new customer creation
  const handleContinueWithNewCustomer = () => {
    setShowMatches(false);
    setCustomerMatches([]);
  };

  const onSubmit: SubmitHandler<CustomerFormState> = async (formValues) => {
    let parsed;
    if (formValues.phone !== "") {
      const { PhoneNumber, warning } = phoneUtils.parse(formValues.phone);
      if (warning === true) {
        parsed = formValues.phone;
        addToastMessage({
          id: createToastMessageID(),
          message: strings.PHONE_TOO_SHORT,
          dialog: false,
          type: "warning",
        });
      } else {
        parsed = PhoneNumber.number;
      }
    }

    // Explicitly create the billing info here to ensure it's not undefined
    let finalBillingInfo: BillingInfo | undefined = undefined;

    if (billingInfo) {
      // If the user has set custom billing info, use that
      finalBillingInfo = billingInfo;
      console.log("Using custom billing info:", finalBillingInfo);
    } else if (locations.length === 1) {
      // If there's exactly one location and no custom billing info,
      // create default billing info from the location and form data
      const firstName = formValues.firstName;
      const lastName = formValues.lastName;
      const customerType = formValues.type;
      const businessName = formValues.name;
      const email = formValues.email || "";
      const phone =
        parsed !== null && parsed !== undefined ? parsed.toString() : "";

      const formattedName = formatCustomerName({
        type: customerType,
        name: businessName,
        firstName,
        lastName,
      });

      finalBillingInfo = {
        name: formattedName,
        email: email,
        phone: phone,
        addressLine1: locations[0]?.addressLine1 ?? "",
        addressLine2: locations[0]?.addressLine2 ?? "",
        city: locations[0]?.city ?? "",
        zipCode: locations[0]?.zipCode ?? "",
        state: locations[0]?.state ?? "",
      };
      console.log(
        "Using default billing info from location:",
        finalBillingInfo,
      );
    } else {
      console.log(
        "No billing info to set - no custom info and no single location",
      );
    }

    const partialCustomerValue: Omit<
      Customer,
      | "timestampCreated"
      | "timestampLastModified"
      | "createdBy"
      | "lastModifiedBy"
    > = {
      name: formatCustomerName({
        type: formValues.type,
        name: formValues.name,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
      }),
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email:
        formValues.email === "" || formValues.email === null
          ? null
          : [formValues.email],
      phone:
        parsed === null || parsed === undefined ? null : [parsed.toString()],
      notes: formValues.notes,
      type: formValues.type,
      tags: handleTags(formValues.tags),
      customData:
        formValues.templateNotes !== "" && formValues.templateNotes != null
          ? { templateNotes: formValues.templateNotes }
          : {},
      isTaxExempt: formValues.isTaxExempt,
      doNotService: formValues.doNotService,
      notificationGroups: [],
      website: "",
      quickbooksID: "",
      deleted: false,
      customerLocations: {},
      billingInfo: finalBillingInfo,
      defaultSendBookingConfirmationEmail:
        formValues.defaultSendBookingConfirmationEmail ?? false,
      defaultSendJobReminderEmail:
        formValues.defaultSendBookingConfirmationEmail ?? false,
    };

    if (
      siteKeyCustomizations.accounting?.currency === "CAD" ||
      siteKeyCustomizations.accounting?.currency === "cad"
    ) {
      partialCustomerValue.isTaxExemptGST = formValues.isTaxExemptGST ?? false;
      partialCustomerValue.isTaxExemptPST = formValues.isTaxExemptPST ?? false;
    }

    try {
      await handleSave(partialCustomerValue);
      // Close dialog if successful.
      closeAndReset();
    } catch (error) {
      setDisplayError(true);
      devLogger.error(error);
    }
  };

  const errorMessage = (
    <ErrorMessage
      message={strings.ERR_CREATE_NEW_CUSTOMER}
      clearMessage={() => setDisplayError(false)}
    />
  );

  // Function to create fake customer and location objects for generating billing info preview
  const getDefaultBillingInfo = () => {
    if (locations.length === 0) return null;

    // Use getValues to access current form field values
    const firstName = getValues("firstName");
    const lastName = getValues("lastName");
    const customerType = getValues("type");
    const businessName = getValues("name");
    const email = getValues("email");
    const phone = getValues("phone");

    // Format the name properly based on customer type
    const formattedName = formatCustomerName({
      type: customerType,
      name: businessName,
      firstName,
      lastName,
    });

    return {
      name: formattedName,
      email: email || "",
      phone: phone || "",
      addressLine1: locations[0]?.addressLine1 ?? "",
      addressLine2: locations[0]?.addressLine2 ?? "",
      city: locations[0]?.city ?? "",
      zipCode: locations[0]?.zipCode ?? "",
      state: locations[0]?.state ?? "",
    };
  };

  // Billing info notification when a single location is added
  const billingInfoNotification = locations.length === 1 && (
    <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4">
      <div className="flex flex-row items-start justify-between">
        <div>
          <h3 className="mb-2 font-medium text-green-800">
            Billing Information
          </h3>
          {!billingInfo ? (
            <p className="text-sm text-green-700">
              The location you've added will be used as the billing address. You
              can set different billing information if needed.
            </p>
          ) : (
            <p className="text-sm text-green-700">
              Custom billing information has been set for this customer.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            const defaultBillingInfo = getDefaultBillingInfo();
            if (defaultBillingInfo) {
              setBillingInfo(billingInfo || defaultBillingInfo);
              setShowBillingInfoDialog(true);
            }
          }}
          className="ml-4 rounded-md border border-green-300 bg-white px-3 py-1 text-sm text-green-600 hover:border-green-400 hover:bg-green-50 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-300"
        >
          {billingInfo ? "Edit Billing Info" : "Set Different Billing Info"}
        </button>
      </div>
      {billingInfo && (
        <div className="mt-2 border-t border-green-200 pt-2">
          <div className="mt-1 text-sm text-green-700">
            <div>Name: {billingInfo.name}</div>
            <div>Phone: {billingInfo.phone}</div>
            <div>Email: {billingInfo.email}</div>
            <div>
              Address:{" "}
              {`${billingInfo.addressLine1}${billingInfo.addressLine2 ? `, ${billingInfo.addressLine2}` : ""}, ${billingInfo.city}, ${billingInfo.state} ${billingInfo.zipCode}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Billing Info Dialog
  const billingInfoDialog = (
    <EditBillingInfoDialog
      billingInfo={
        billingInfo ||
        getDefaultBillingInfo() || {
          name: "",
          email: "",
          phone: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          zipCode: "",
        }
      }
      isDialogOpen={showBillingInfoDialog}
      isSubmitting={false}
      customerLocationOptions={
        locations.length > 0
          ? locations.map((loc, idx) => ({
              ...loc,
              id: `temp-${idx}`,
              refPath: ``,
              timestampCreated: Timestamp.now(),
              timestampLastModified: Timestamp.now(),
              customerID: "temp",
              createdBy: "",
              lastModifiedBy: "",
            }))
          : null
      }
      closeDialog={() => setShowBillingInfoDialog(false)}
      handleUpdateBillingInfo={(updatedBillingInfo) => {
        console.log("Setting custom billing info:", updatedBillingInfo);
        // Make sure we're setting a fresh copy
        setBillingInfo({ ...updatedBillingInfo });
        setShowBillingInfoDialog(false);
      }}
    />
  );

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      className={props.className}
    >
      {/* Field: Type */}
      <div>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <BaseInputSelect
              inputName="type"
              text="Type"
              admin={true}
              required={true}
              className="capitalize"
              {...field}
            >
              {/* The empty string for value tricks validation into failing */}
              <option value="" disabled>
                Select a type
              </option>
              {customerTypes.map((type) => {
                return (
                  <option key={type} value={type}>
                    {type}
                  </option>
                );
              })}
            </BaseInputSelect>
          )}
        />
        {errors.type?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.type.message }}
            </StyledMessage>
          </div>
        )}
      </div>

      {/* Rest of the form fields */}
      {watchType === "commercial" && (
        /* Field: Name */
        <div>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <BaseInputText
                text="Business Name"
                inputName="name"
                admin={true}
                required={true}
                {...field}
                onChange={(event) => {
                  field.onChange(event.target.value);
                  handleSearchName();
                }}
              />
            )}
          />
          {errors.name?.message && (
            <div className="mt-2 text-sm">
              <StyledMessage type="error">
                {{ message: errors.name.message }}
              </StyledMessage>
            </div>
          )}
        </div>
      )}

      {/* First Name Field */}
      <div>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text={
                watchType === "commercial" ? "Contact First Name" : "First Name"
              }
              inputName="firstName"
              admin={true}
              required={true}
              {...field}
              onChange={(event) => {
                field.onChange(event.target.value);
                handleSearchName();
              }}
            />
          )}
        />
        {errors.firstName?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.firstName.message }}
            </StyledMessage>
          </div>
        )}
      </div>

      {/* Last Name Field */}
      <div>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text={
                watchType === "commercial" ? "Contact Last Name" : "Last Name"
              }
              inputName="lastName"
              admin={true}
              required={true}
              {...field}
              onChange={(event) => {
                field.onChange(event.target.value);
                handleSearchName();
              }}
            />
          )}
        />
        {errors.lastName?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.lastName.message }}
            </StyledMessage>
          </div>
        )}
      </div>

      {/* Email Field */}
      <div>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text="Email"
              inputName="email"
              admin={true}
              required={false}
              {...field}
              value={field.value === null ? "" : field.value}
            />
          )}
        />
        {errors.email?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.email.message }}
            </StyledMessage>
          </div>
        )}
      </div>

      {/* Phone Field */}
      <div>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text="Phone"
              inputName="phone"
              admin={true}
              required={false}
              {...field}
            />
          )}
        />
        {errors.phone?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.phone.message }}
            </StyledMessage>
          </div>
        )}
      </div>

      {/* Customer Matches - Show when potential duplicates are found */}
      {showMatches && customerMatches.length > 0 && (
        <div className="col-span-3">
          <ExistingCustomerMatches
            matchingCustomers={customerMatches}
            onSelectCustomer={handleSelectExistingCustomer}
            onContinueWithNew={handleContinueWithNewCustomer}
            isVisible={showMatches}
          />
        </div>
      )}

      {/* Field: Notes */}
      <div>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text="Notes"
              inputName="notes"
              admin={true}
              required={false}
              {...field}
            />
          )}
        />
        {errors.notes?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.notes.message }}
            </StyledMessage>
          </div>
        )}
      </div>
      {/* Field: Tags */}
      <div>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <BaseInputText
              text="Tags"
              inputName="tags"
              admin={true}
              required={false}
              placeholder="Insert your tags, separated by comma"
              {...field}
            />
          )}
        />
        {errors.tags?.message && (
          <div className="mt-2 text-sm">
            <StyledMessage type="error">
              {{ message: errors.tags.message }}
            </StyledMessage>
          </div>
        )}
      </div>
      {/* Field: Template Notes */}
      {siteKeyCustomizations?.customerCustomizations?.templateNotes === true ? (
        <div className="col-span-3">
          <Controller
            name="templateNotes"
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                text="Job Template Notes"
                inputName="templateNotes"
                admin={true}
                required={false}
                rows={3}
                {...field}
              />
            )}
          />
          {errors.templateNotes?.message && (
            <div className="mt-2 text-sm">
              <StyledMessage type="error">
                {{ message: errors.templateNotes.message }}
              </StyledMessage>
            </div>
          )}
        </div>
      ) : null}
      {/* SWITCH GROUP */}
      <div className="col-span-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          {/* Field: Taxable */}
          {siteKeyCustomizations.accounting?.currency !== "CAD" &&
            siteKeyCustomizations.accounting?.currency !== "cad" && (
              <div>
                <Controller
                  name="isTaxExempt"
                  control={control}
                  render={({ field }) => (
                    <StyledSwitchGroup
                      readableName={strings.TAX_EXEMPT}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      ref={field.ref}
                      checked={field.value}
                      id="isTaxExempt"
                      name={field.name}
                    />
                  )}
                />
              </div>
            )}
          {(siteKeyCustomizations.accounting?.currency === "CAD" ||
            siteKeyCustomizations.accounting?.currency === "cad") && (
            <div>
              <Controller
                name="isTaxExemptPST"
                control={control}
                render={({ field }) => (
                  <StyledSwitchGroup
                    readableName={strings.TAX_EXEMPT_PST}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    checked={field.value ?? false}
                    id="isTaxExempt"
                    name={field.name}
                  />
                )}
              />
            </div>
          )}
          {(siteKeyCustomizations.accounting?.currency === "CAD" ||
            siteKeyCustomizations.accounting?.currency === "cad") && (
            <div>
              <Controller
                name="isTaxExemptGST"
                control={control}
                render={({ field }) => (
                  <StyledSwitchGroup
                    readableName={strings.TAX_EXEMPT_GST}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    checked={field.value ?? false}
                    id="isTaxExempt"
                    name={field.name}
                  />
                )}
              />
            </div>
          )}
          <div>
            <Controller
              name="doNotService"
              control={control}
              render={({ field }) => (
                <StyledSwitchGroup
                  readableName={strings.DO_NOT_SERVICE}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  checked={field.value ?? false}
                  id="doNotService"
                  name={field.name}
                />
              )}
            />
          </div>
        </div>
        {/* EMAIL CONFIRMATION - REMINDER */}
        <div className="flex flex-col gap-2">
          {siteKeyCustomizations.sendJobBookingConfirmationToCustomers && (
            <div>
              <Controller
                name="defaultSendBookingConfirmationEmail"
                control={control}
                render={({ field }) => (
                  <StyledSwitchGroup
                    readableName={
                      strings.DEFAULT_SEND_BOOKING_CONFIRMATION_EMAIL
                    }
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    checked={field.value ?? false}
                    id="defaultSendBookingConfirmationEmail"
                    name={field.name}
                  />
                )}
              />
            </div>
          )}
          {siteKeyCustomizations.sendTaskReminderToCustomers && (
            <div>
              <Controller
                name="defaultSendJobReminderEmail"
                control={control}
                render={({ field }) => (
                  <StyledSwitchGroup
                    readableName={strings.DEFAULT_SEND_24H_REMINDER_EMAIL}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    checked={field.value ?? false}
                    id="defaultSendJobReminderEmail"
                    name={field.name}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
      {props.children
        ? props.children.AddNewCustomerLocationDialog(watchType)
        : null}
      {billingInfoNotification}
      {/* Action Buttons */}
      <div className="flex w-full flex-col items-center justify-between gap-4 xs:flex-row sm:col-span-3 sm:col-start-1">
        <BaseButtonSecondary
          type="button"
          className="w-full justify-center uppercase"
          onClick={closeAndReset}
        >
          {strings.buttons.CANCEL}
        </BaseButtonSecondary>

        <BaseButtonPrimary
          type="submit"
          formNoValidate
          disabled={isSubmitting}
          isBusy={isSubmitting}
          busyText={strings.buttons.BUSY_SAVING}
          className="w-full justify-center uppercase"
        >
          {strings.buttons.SAVE}
        </BaseButtonPrimary>
      </div>
      {displayError ? (
        <span className="absolute bottom-10 left-1/2 w-3/4 -translate-x-1/2 sm:w-96">
          {errorMessage}
        </span>
      ) : null}
      {billingInfoDialog}
    </form>
  );
}
